import React, { useRef } from 'react';
import objStr from 'obj-str';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import navigationService from '@/services/navigation-service';

import styles from './MegaMenu.module.scss';
import type * as Types from '@/pages/mijn-carel-lurvink/catalogus/stores/types';

interface MegaMenuCategoryList {
  categories: Types.Category[];
  displayMode?: string;
  level?: number;
}

export const MegaMenuCategoryList = ({ categories, displayMode = 'WithSubCategories', level = 1 }: MegaMenuCategoryList) => {
  const { t } = useTranslation();
  const router = useRouter();
  const levelRef = useRef(level);
  const displayWithSubCategories = displayMode === 'WithSubCategories';

  const classNames = objStr({
    [styles.subMenu]: level === 1,
    [styles.withSubcategories]: displayWithSubCategories,
    [styles[`level-${levelRef.current}`]]: true,
    'submenu lvl-1 with-subcategories': true
  });

  return (
    <ul className={classNames} data-testid={`submenu:level-${levelRef.current}`}>
      {categories.map((category: Types.Category) => {
        const subCategories = navigationService.getChildCategoriesByParentCode(category.code);
        const hasSubCategory = category.childCodes.length > 0;

        const isActive = router.asPath.includes(category.urlSet.web);

        const itemClassNames = objStr({
          [styles[`level-${levelRef.current}`]]: true,
          [styles.hasSubCategory]: hasSubCategory && !displayWithSubCategories,
          [styles.withSubcategories]: displayWithSubCategories,
          [styles.active]: isActive
        });

        return (
          <li className={`level-${levelRef.current} text-serif with-subcategories`} key={category.code}>
            <a
              title={category.name}
              href={category.urlSet.web}
              className={itemClassNames}
              aria-haspopup="true"
              data-testid={`submenu:${category.code}`}
            >
              <span>{category.name}</span>
            </a>
            {hasSubCategory && (
              <>
                {displayWithSubCategories && (
                  <ul className="subcategories">
                    {subCategories.slice(0, 4).map((category: Types.Category) => {
                      const isActive = router.asPath.includes(category.urlSet.web);
                      return (
                        <li key={category.code} className="text-serif">
                          <a
                            title={category.name}
                            href={category.urlSet.web}
                            aria-haspopup="true"
                            data-testid={`submenu:${category.code}`}
                            className={`${isActive ? 'text-theme' : ''}`}
                          >
                            <span>{category.name}</span>
                          </a>
                        </li>
                      );
                    })}
                    <li className="text-serif">
                      <a title={category.name} href={category.urlSet.web}>
                        <b>
                          {/* TODO: Translate */}
                          <i className="text-gray-600">
                            {t('common:All')} {category.name}
                          </i>
                        </b>
                      </a>
                    </li>
                  </ul>
                )}
                {!displayWithSubCategories && <MegaMenuCategoryList categories={subCategories} level={levelRef.current + 1} />}
              </>
            )}
          </li>
        );
      })}
    </ul>
  );
};
